import React, { useState } from "react";
import moment from "moment";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import CloseIcon from "@mui/icons-material/Close";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormControl from "@mui/material/FormControl";
import TextField from "@mui/material/TextField";
import LinearProgress from "@mui/material/LinearProgress";
import Alert from "@mui/material/Alert";
import StorageLocationAutocomplete from "src/views/capture/RackLocations/StorageLocationAutocomplete/StorageLocationAutocomplete";
import useMutation from "src/hooks/useMutation";
import ItemsToProcessList from "./ItemsToProcessList";
import {
  CREATE_INVENTORY_TRANSACTION_MUTATION,
  UPDATE_SHIPMENT_ITEMS_MUTATION,
  UPDATE_SHIPMENT_MUTATION,
} from "src/constants/graphql/mutations";
import { serverFormatSeconds } from "src/utils/dates";
import { getShipmentValues } from "src/utils/shipment";

const styles = {
  closeButton: {
    position: "absolute",
    right: 8,
    top: 8,
  },
};

export default function BulkInputLocationModal({
  open,
  onClose,
  shipmentItems,
  project,
  shipment,
  shipmentId,
}) {
  const isOnsiteInventory = shipment?.shipmentStatus === 'onsite_inventory';
  const defaultStrategy = isOnsiteInventory ? 'expected' : 'origin';
  
  const [location, setLocation] = useState({
    warehouse: null,
    warehouse_locations: [],
  });
  const [quantityStrategy, setQuantityStrategy] = useState(defaultStrategy);
  const [singleQuantity, setSingleQuantity] = useState("");
  const [processing, setProcessing] = useState(false);
  const [progress, setProgress] = useState({ current: 0, total: 0 });
  const [errors, setErrors] = useState([]);
  const [createTransaction] = useMutation(CREATE_INVENTORY_TRANSACTION_MUTATION);
  const [updateShipmentItem] = useMutation(UPDATE_SHIPMENT_ITEMS_MUTATION);
  const [updateShipment] = useMutation(UPDATE_SHIPMENT_MUTATION);

  const handleLocationChange = newValue => {
    setLocation(newValue);
  };

  const handleApply = async () => {
    const itemsToProcess = shipmentItems.filter(item => {
      const hasLocationForShipment = item.asset?.rackLocations?.some(
        v => v.shipment_id === shipmentId
      );
      return !hasLocationForShipment;
    });

    if (itemsToProcess.length === 0) {
      setErrors(["No items to process (all items already have locations for this shipment)."]);
      return;
    }

    setProcessing(true);
    setProgress({ current: 0, total: itemsToProcess.length });
    setErrors([]);

    const newErrors = [];

    // Convert shipment status from onsite_inventory to in_progress if needed
    if (isOnsiteInventory) {
      try {
        const input = {
          ...getShipmentValues(shipment),
          startTime: moment(new Date()).format(serverFormatSeconds),
          draft: false,
          shipmentStatus: 'in_progress',
        };

        await updateShipment({
          variables: { id: shipment.id, input },
        });
      } catch (err) {
        console.error("Error updating shipment status:", err);
        newErrors.push(`Failed to update shipment status: ${err.message}`);
        setProcessing(false);
        setErrors(newErrors);
        return;
      }
    }

    for (let i = 0; i < itemsToProcess.length; i++) {
      const item = itemsToProcess[i];
      setProgress({ current: i + 1, total: itemsToProcess.length });

      let quantity;
      if (quantityStrategy === "single") {
        quantity = parseInt(singleQuantity, 10);
      } else if (quantityStrategy === "expected") {
        quantity = item.expectedQuantity || 1;
      } else {
        quantity = item.asset.quantity || 1;
      }

      try {
        const rackLocationPayload = [
          {
            warehouse: location.warehouse,
            warehouse_locations: location.warehouse_locations,
            sub_qty: quantity,
            shipment_id: shipment.shipmentId,
          },
        ];

        await createTransaction({
          variables: {
            code: item.asset.assetId,
            quantity: quantity,
            notes: "Bulk Input Location",
            senderName: "",
            carrierName: "",
            trackingNumber: "",
            orderNumber: "",
            poNumber: "",
            rackLocations: rackLocationPayload,
            rackLocationsChanges: rackLocationPayload,
            shipmentItemId: item.id,
            assetId: item.asset.id,
            actionSource: "transaction_modal",
          },
        });

        await updateShipmentItem({
          variables: {
            id: item.id,
            quantity: quantity,
          },
        });
      } catch (err) {
        console.error("Error processing item:", item.id, err);
        newErrors.push(`Failed to process item ${item.asset?.name || item.id}: ${err.message}`);

        break;
      }
    }

    setProcessing(false);

    if (newErrors.length > 0) {
      setErrors(newErrors);
    } else {
      onClose();
    }
  };

  const isQuantityValid =
    quantityStrategy !== "single" || (singleQuantity && parseInt(singleQuantity) > 0);
  const isLocationValid = location.warehouse && location.warehouse_locations?.length > 0;
  const isApplyDisabled = processing || !isLocationValid || !isQuantityValid;

  return (
    <Dialog 
      open={open} 
      onClose={!processing ? onClose : undefined} 
      maxWidth={false}
      sx={{ '& .MuiDialog-paper': { width: '550px', maxWidth: '90vw' } }}
    >
      <DialogTitle>
        <Typography fontSize={20} fontWeight={500} color="text.primary" align="center">
          Bulk Receive Into Warehouse
        </Typography>
        <IconButton onClick={onClose} size="large" sx={styles.closeButton}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 2, display: "flex", flexDirection: "column", gap: 3 }}>
          <StorageLocationAutocomplete
            projectId={project?.id}
            value={location}
            onChange={handleLocationChange}
          />
          {!isOnsiteInventory && (
            <Box>
              <FormControl component="fieldset">
                <RadioGroup
                  value={quantityStrategy}
                  onChange={e => setQuantityStrategy(e.target.value)}
                >
                  <FormControlLabel
                    value="single"
                    control={<Radio />}
                    label="Single Quantity (Apply to all)"
                  />
                  {quantityStrategy === "single" && (
                    <Box sx={{ ml: 4, mb: 1 }}>
                      <TextField
                        label="Quantity"
                        type="number"
                        size="small"
                        value={singleQuantity}
                        onChange={e => setSingleQuantity(e.target.value)}
                        disabled={processing}
                      />
                    </Box>
                  )}
                  <FormControlLabel value="origin" control={<Radio />} label="Use Origin Quantity" />
                </RadioGroup>
              </FormControl>
            </Box>
          )}

          <ItemsToProcessList
            shipmentItems={shipmentItems}
            quantityStrategy={quantityStrategy}
            singleQuantity={singleQuantity}
            shipment={shipment}
          />

          {processing && (
            <Box>
              <Typography variant="body2" gutterBottom>
                Processing {progress.current} of {progress.total}
              </Typography>
              <LinearProgress
                variant="determinate"
                value={(progress.current / progress.total) * 100}
              />
            </Box>
          )}

          {errors.length > 0 && (
            <Box>
              {errors.map((error, idx) => (
                <Alert severity="error" key={idx} sx={{ mb: 1 }}>
                  {error}
                </Alert>
              ))}
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button onClick={onClose} disabled={processing} variant="outlined">
          Cancel
        </Button>
        <Button onClick={handleApply} variant="contained" disabled={isApplyDisabled}>
          Apply
        </Button>
      </DialogActions>
    </Dialog>
  );
}
